import { StateCreator } from 'zustand';

import { Store } from 'store';
import { RangeNumber } from 'lib/types';
import { toggleSetValue } from 'lib/utils';
import { PolygonSchema } from 'api/types';

export interface BuyApartmentSlice {
  buyApartmentSearch: string;
  setBuyApartmentSearch: (value: string) => void;

  selectedComplexes: Set<number>;
  toggleComplexSelection: (id: number) => void;

  priceRange: RangeNumber;
  setPriceRange: (r: RangeNumber) => void;

  pricePerSqMRange: RangeNumber;
  setPricePerSqMRange: (r: RangeNumber) => void;

  diffWithMarketPrice: number | undefined;
  setDiffWithMarketPrice: (d: number) => void;

  marketPriceRange: RangeNumber;
  setMarketPriceRange: (r: RangeNumber) => void;

  areaSqFootageRange: RangeNumber;
  setAreaSqFootageRange: (r: RangeNumber) => void;

  selectedApartmentPlan: Set<number>;
  setApartmentPlan: (id: number) => void;

  selectedPolygon: PolygonSchema[] | null;
  setSelectedPolygon: (value: PolygonSchema[] | null) => void;

  selectedRoomTypes: Set<number>;
  toggleSelectedRoomTypes: (id: number) => void;
  clearRoomTypes: () => void;

  fieldSort: string | undefined;
  setFieldSort: (value: string | undefined) => void;

  directionSort: string | undefined;
  setDirectionSort: (value: string | undefined) => void;

  setFilters: (
    filters: Partial<
      Pick<
        BuyApartmentSlice,
        | 'pricePerSqMRange'
        | 'priceRange'
        | 'marketPriceRange'
        | 'areaSqFootageRange'
        | 'diffWithMarketPrice'
        | 'selectedRoomTypes'
      >
    >,
  ) => void;
}

const createBuyApartmentSlice: StateCreator<
  Store,
  [['zustand/immer', never]],
  [],
  BuyApartmentSlice
> = (set) => ({
  buyApartmentSearch: '',
  setBuyApartmentSearch: (value) => set({ buyApartmentSearch: value }),

  selectedComplexes: new Set(),
  toggleComplexSelection: (id) => {
    set((s) => toggleSetValue(s.selectedComplexes, id));
  },

  fieldSort: undefined,
  setFieldSort: (value: string | undefined) => {
    set({ fieldSort: value });
  },

  directionSort: undefined,
  setDirectionSort: (value: string | undefined) => {
    set({ directionSort: value });
  },

  priceRange: {},
  setPriceRange: (priceRange) =>
    set((s) => {
      s.priceRange.min = priceRange.min;
      s.priceRange.max = priceRange.max;
    }),

  pricePerSqMRange: {},
  setPricePerSqMRange: (priceRange) =>
    set((s) => {
      s.priceRange.min = priceRange.min;
      s.priceRange.max = priceRange.max;
    }),

  diffWithMarketPrice: undefined,
  setDiffWithMarketPrice: (diff) => set({ diffWithMarketPrice: diff }),

  marketPriceRange: {},
  setMarketPriceRange: (priceRange) =>
    set((s) => {
      s.marketPriceRange.min = priceRange.min;
      s.marketPriceRange.max = priceRange.max;
    }),

  areaSqFootageRange: {},
  setAreaSqFootageRange: (areaSqFootageRange) =>
    set((s) => {
      s.areaSqFootageRange.min = areaSqFootageRange.min;
      s.areaSqFootageRange.max = areaSqFootageRange.max;
    }),

  selectedApartmentPlan: new Set(),
  setApartmentPlan: (id) => {
    set((s) => toggleSetValue(s.selectedApartmentPlan, id));
  },

  selectedRoomTypes: new Set(),
  toggleSelectedRoomTypes: (id) => {
    set((s) => toggleSetValue(s.selectedRoomTypes, id));
  },
  clearRoomTypes: () => set({ selectedRoomTypes: new Set() }),

  selectedPolygon: null,
  setSelectedPolygon: (value: PolygonSchema[] | null) => set({ selectedPolygon: value }),

  setFilters: (filters) => set((s) => void Object.assign(s, filters)),
});

export default createBuyApartmentSlice;
